import { ValidatorSchema } from "@constants/validators";
import { zodResolver } from "@hookform/resolvers/zod";
import useAuthSession from "@hooks/useAuthSession";
import useFundingProfile from "@hooks/useFundingProfile";
import { useMemberSettings } from "@hooks/useMemberSettings";
import usePaymentInfo from "@hooks/usePaymentInfo";
import { MemberSettingFundingMode, MemberSettingNameMode } from "kz-ui-sdk";
import { useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { FundingProfileFieldValues } from "../types";

export const useEWalletFundingProfileFormController = () => {
  const { phoneMinLength, phoneMaxLength, phoneStartWith, phoneSimpleError, nameMode } = useMemberSettings();
  const { fundingProfileList } = useFundingProfile();
  const { getPaymentMethodByKey } = usePaymentInfo();
  const { legalName } = useAuthSession();

  const checkIfFundingProfileExists = useCallback(
    (accountNo: string, accountType: string): boolean => {
      const foundFundingProfile = fundingProfileList?.find(
        (profile) =>
          profile.accNo === accountNo &&
          profile.type === getPaymentMethodByKey(accountType)?.type &&
          profile.key === accountType,
      );

      return !!foundFundingProfile;
    },
    [fundingProfileList, getPaymentMethodByKey],
  );

  const formSchema = useMemo(() => {
    let schema = z.object({
      accountNo: ValidatorSchema.payment.eWallet.accountNo({
        minLength: phoneMinLength,
        maxLength: phoneMaxLength,
        startWith: phoneStartWith,
        simpleError: phoneSimpleError,
      }),
      accountType: ValidatorSchema.payment.eWallet.accountType,
      isDefault: ValidatorSchema.payment.eWallet.isDefault,
    });

    // Add name fields if legal name is not fulfilled
    if (!legalName) {
      if (nameMode === MemberSettingNameMode.FullInFirst) {
        schema = schema.extend({ accountName: ValidatorSchema.accountName });
      }
      if (nameMode === MemberSettingNameMode.FirstLast) {
        schema = schema.extend({ firstName: ValidatorSchema.firstName, lastName: ValidatorSchema.lastName });
      }
    }

    return schema.refine((data) => !checkIfFundingProfileExists(data.accountNo, data.accountType), {
      message: `${MemberSettingFundingMode.EWalletOnly} already added`,
      path: ["accountNo"],
    });
  }, [
    phoneMinLength,
    phoneMaxLength,
    phoneStartWith,
    phoneSimpleError,
    nameMode,
    legalName,
    checkIfFundingProfileExists,
  ]);

  const form = useForm<FundingProfileFieldValues>({
    mode: "onChange",
    resolver: zodResolver(formSchema),
    defaultValues: {
      accountNo: "",
      accountType: "",
      accountDescription: "",
      isDefault: true,
      firstName: "",
      lastName: "",
      accountName: "",
    }
  });

  return { form, schema: formSchema };
};
